import { useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MapPin, ExternalLink } from "lucide-react";
import { nearestBranchForState, COMPANY } from "@/data/branches";

const INTERESTS = [
  "Digital Board (65/75/86 inch)",
  "Interactive Panel",
  "Smart Classroom Setup",
  "Coaching Institute Setup",
  "Bulk Order (5+ units)",
  "Other",
];

export default function LeadForm({
  defaultCity = "",
  defaultState = "",
  defaultInterest = "",
  sourcePage = "",
  variant = "card",
  title = "Book a Free Demo",
  subtitle = "Talk to our specialist within 24 hours.",
  testIdPrefix = "lead-form",
  onSuccess,
}) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: defaultCity,
    state: defaultState,
    interest: defaultInterest || INTERESTS[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const nearest = useMemo(
    () => nearestBranchForState(form.state || defaultState),
    [form.state, defaultState]
  );

  const update = (k) => (e) =>
    setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        interest: form.interest,
        source_page: sourcePage,
        nearest_center: nearest?.primary?.slug || null,
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.city.trim()) payload.city = form.city.trim();
      if (form.state.trim()) payload.state = form.state.trim();
      if (form.message.trim()) payload.message = form.message.trim();
      await api.post("/leads", payload);
      setDone(true);
      toast.success("Thanks! Our team will call you soon.");
      if (typeof onSuccess === "function") {
        try { onSuccess(); } catch {}
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        className={`${
          variant === "card"
            ? "bg-white border border-slate-200 rounded-lg p-8 shadow-sm"
            : ""
        } text-center`}
        data-testid={`${testIdPrefix}-success`}
      >
        <CheckCircle2 className="mx-auto text-emerald-500" size={48} />
        <h3 className="mt-3 text-xl font-bold">Request Received</h3>
        <p className="text-slate-600 mt-2">
          Our specialist from <strong>{nearest?.primary?.name || "Smart Infovision"}</strong> will
          reach out within a few hours. Want a faster response?{" "}
          <a
            className="text-slate-900 underline"
            href={COMPANY.whatsapp_link}
            target="_blank"
            rel="noreferrer"
          >
            WhatsApp us
          </a>
          .
        </p>
      </div>
    );
  }

  const wrapper =
    variant === "card"
      ? "bg-white border border-slate-200 rounded-lg p-6 md:p-8 shadow-sm"
      : "";

  return (
    <form onSubmit={submit} className={wrapper} data-testid={testIdPrefix}>
      {title && (
        <div className="mb-4">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>
            {title}
          </h3>
          {subtitle && (
            <p className="text-sm text-slate-600 mt-1">{subtitle}</p>
          )}
        </div>
      )}

      {nearest?.primary && (
        <div
          className="mb-5 rounded-md border border-slate-200 bg-slate-50/60 p-3.5"
          data-testid={`${testIdPrefix}-nearest-center`}
        >
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-slate-900 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-900">
                Nearest Experience Center
              </div>
              <div className="text-sm font-bold text-slate-900 mt-0.5">
                {nearest.primary.name}
              </div>
              <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                {nearest.primary.address}
              </div>
              <a
                href={nearest.primary.map}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-900 hover:text-[#EA580C]"
                data-testid={`${testIdPrefix}-nearest-map`}
              >
                Open in Maps <ExternalLink size={11} />
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          required
          placeholder="Your Name *"
          value={form.name}
          onChange={update("name")}
          data-testid={`${testIdPrefix}-name`}
        />
        <Input
          required
          placeholder="Phone Number *"
          value={form.phone}
          onChange={update("phone")}
          data-testid={`${testIdPrefix}-phone`}
        />
        <Input
          type="email"
          placeholder="Email (optional)"
          value={form.email}
          onChange={update("email")}
          data-testid={`${testIdPrefix}-email`}
        />
        <Input
          placeholder="City"
          value={form.city}
          onChange={update("city")}
          data-testid={`${testIdPrefix}-city`}
        />
      </div>
      <div className="mt-3">
        <Select
          value={form.interest}
          onValueChange={(v) => setForm({ ...form, interest: v })}
        >
          <SelectTrigger data-testid={`${testIdPrefix}-interest`}>
            <SelectValue placeholder="I am interested in..." />
          </SelectTrigger>
          <SelectContent>
            {INTERESTS.map((i) => (
              <SelectItem key={i} value={i}>
                {i}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Textarea
        placeholder="Message (school name, batch size, requirements...)"
        rows={3}
        className="mt-3"
        value={form.message}
        onChange={update("message")}
        data-testid={`${testIdPrefix}-message`}
      />
      <Button
        type="submit"
        disabled={loading}
        className="w-full mt-4 bg-[#EA580C] hover:bg-[#C2410C] text-white h-11 text-base"
        data-testid={`${testIdPrefix}-submit`}
      >
        {loading ? <Loader2 className="animate-spin" /> : "Request Free Demo"}
      </Button>
      <p className="text-[11px] text-slate-500 mt-3 text-center">
        By submitting, you agree to be contacted on WhatsApp / Call.
      </p>
    </form>
  );
}
